import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Users, ChevronRight, Star, Share2, Bookmark, CheckCircle, MessageCircle, X, Copy, Linkedin } from 'lucide-react';

function EventsPage({ setCurrentPage }) {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Registration form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    linkedin: '',
    excitement: ''
  });

  // Single featured event data
  const featuredEvent = {
    id: 1,
    title: 'Nerds Meetup - Tech & Innovation Summit',
    category: 'Networking',
    date: 'Nov 22, 2025',
    time: '9:30 AM - 4:00 PM',
    city: 'Noida',
    venue: 'Microsoft India (R&D) Pvt. Ltd., Noida',
    fullAddress: 'TOWER-EXPRESS TRADE, 1, 1A, Slip Rd, Film City, Sector 16A, Noida, Uttar Pradesh 201301, India',
    attendees: 89,
    maxAttendees: 120,
    type: 'In-Person',
    description: 'An evening packed with tech, talks, delicious food, and nerdy conversations where developers, creators, and innovators come together to learn, share, and connect.',
    detailedDescription: `We're all set for Nerds Meetup, happening on 22nd November 2025 at the Microsoft Office, Noida!

Get ready for an evening packed with tech, talks, delicious food, and nerdy conversations — where developers, creators, and innovators come together to learn, share, and connect.

💡 Expect insightful sessions, product showcases, interactive networking, and a few fun surprises along the way 😉

Whether you're a student, professional, or startup founder, this is your chance to be part of one of the most vibrant and fast-growing tech communities.`,
    price: 'Free',
    status: 'Approval Required',
    organizer: {
      name: 'Nerds Room',
      avatar: '👨‍💻',
      hosts: ['Kumari Vrishti', 'Aditya'],
      rating: 4.8,
      reviews: 127
    },
    features: [
      'Tech Talks & Sessions',
      'Product Showcases',
      'Networking Sessions',
      'Food & Beverages',
      'Interactive Workshops'
    ],
    approvalNote: 'Your registration is subject to approval by the host.'
  };

  const handleRegister = () => {
    setShowRegistrationForm(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    setIsRegistered(true);
    setShowRegistrationForm(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    { platform: 'WhatsApp', icon: '💬', color: '#25D366' },
    { platform: 'LinkedIn', icon: '💼', color: '#0077B5' },
    { platform: 'Twitter', icon: '🐦', color: '#1DA1F2' },
    { platform: 'Email', icon: '📧', color: '#EA4335' }
  ];

  const descriptionOptions = [
    'Student',
    'Professional Developer',
    'Startup Founder',
    'Product Manager',
    'Designer',
    'Investor',
    'Tech Enthusiast',
    'Other'
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, #4A7C5B 0%, #2C5F3F 100%)', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        
        {/* Main Event Card */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          marginBottom: '3rem'
        }}>
          
          {/* Header with Green Gradient */}
          <div style={{
            background: 'linear-gradient(135deg, #4A7C5B 0%, #2C5F3F 100%)',
            padding: '3rem 3rem 2rem',
            color: 'white',
            position: 'relative'
          }}>
            {/* Action Buttons */}
            <div style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              display: 'flex',
              gap: '1rem'
            }}>
              <button
                onClick={() => setIsBookmarked(!isBookmarked)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  color: isBookmarked ? '#FFD700' : 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                <Bookmark size={20} fill={isBookmarked ? 'currentColor' : 'none'} />
              </button>
              
              <button 
                onClick={handleShare}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                <Share2 size={20} />
              </button>
            </div>

            {/* Event Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '0.5rem 1.25rem',
              borderRadius: '50px',
              marginBottom: '1.5rem',
              backdropFilter: 'blur(10px)',
              fontSize: '0.85rem',
              fontWeight: '700'
            }}>
              <Star size={16} fill="currentColor" />
              Featured in Noida
            </div>

            {/* Event Title */}
            <h1 style={{
              fontSize: '3rem',
              fontWeight: '800',
              marginBottom: '1rem',
              lineHeight: 1.2,
              letterSpacing: '-1px'
            }}>
              {featuredEvent.title}
            </h1>

            {/* Quick Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              opacity: 0.95
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={20} />
                <span style={{ fontWeight: '600' }}>{featuredEvent.date}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={20} />
                <span style={{ fontWeight: '600' }}>{featuredEvent.time}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <MapPin size={20} />
                <span style={{ fontWeight: '600' }}>{featuredEvent.city}</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '0'
          }}>
            
            {/* Left Column - Event Details */}
            <div style={{ padding: '3rem' }}>
              
              {/* Organizer Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '2.5rem',
                padding: '1.5rem',
                background: '#F8FAFC',
                borderRadius: '12px',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  background: 'linear-gradient(135deg, #4A7C5B 0%, #2C5F3F 100%)',
                  borderRadius: '12px',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {featuredEvent.organizer.avatar}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      color: '#1F2937',
                      margin: 0
                    }}>
                      {featuredEvent.organizer.name}
                    </h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color: '#6B7280',
                      fontSize: '0.9rem'
                    }}>
                      <Star size={16} fill="#F59E0B" color="#F59E0B" />
                      <span>{featuredEvent.organizer.rating}</span>
                      <span>({featuredEvent.organizer.reviews} reviews)</span>
                    </div>
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '0.95rem' }}>
                    Hosted by {featuredEvent.organizer.hosts.join(', ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button style={{
                    background: 'transparent',
                    border: '2px solid #E5E7EB',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    color: '#4B5563',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#4A7C5B';
                    e.target.style.color = '#4A7C5B';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.color = '#4B5563';
                  }}
                  >
                    <MessageCircle size={16} />
                    Contact
                  </button>
                </div>
              </div>

              {/* Event Features */}
              <div style={{ marginBottom: '2.5rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: '#1F2937'
                }}>
                  What to Expect
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  {featuredEvent.features.map((feature, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '1rem',
                      background: '#F0F9F4',
                      borderRadius: '8px',
                      fontWeight: '500',
                      color: '#065F46',
                      border: '1px solid #BBF7D0'
                    }}>
                      <CheckCircle size={18} color="#059669" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* About Event */}
              <div style={{ marginBottom: '2.5rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: '#1F2937'
                }}>
                  About this Event
                </h3>
                <div style={{
                  color: '#4B5563',
                  lineHeight: 1.7,
                  fontSize: '1.05rem',
                  whiteSpace: 'pre-line'
                }}>
                  {featuredEvent.detailedDescription}
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: '#1F2937'
                }}>
                  Location
                </h3>
                <div style={{
                  padding: '1.5rem',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <MapPin size={20} color="#4A7C5B" style={{ marginTop: '0.25rem' }} />
                    <div>
                      <div style={{
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        color: '#1F2937'
                      }}>
                        {featuredEvent.venue}
                      </div>
                      <div style={{
                        color: '#6B7280',
                        lineHeight: 1.5
                      }}>
                        {featuredEvent.fullAddress}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Registration Card */}
            <div style={{
              background: '#F8FAFC',
              padding: '2.5rem',
              borderLeft: '1px solid #E5E7EB'
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
                position: 'sticky',
                top: '2rem'
              }}>
                
                {/* Status Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: '#FEF3F2',
                  color: '#DC2626',
                  padding: '0.5rem 1rem',
                  borderRadius: '50px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem'
                }}>
                  ⚡ {featuredEvent.status}
                </div>

                {/* Price */}
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: '#1F2937',
                  marginBottom: '1.5rem'
                }}>
                  {featuredEvent.price}
                </div>

                {/* Attendees */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem',
                  padding: '1rem',
                  background: '#F0FDF4',
                  borderRadius: '8px',
                  border: '1px solid #BBF7D0'
                }}>
                  <Users size={20} color="#059669" />
                  <div>
                    <div style={{
                      fontWeight: '600',
                      color: '#065F46'
                    }}>
                      {featuredEvent.attendees} people attending
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#047857'
                    }}>
                      {featuredEvent.maxAttendees - featuredEvent.attendees} spots left
                    </div>
                  </div>
                </div>

                {/* Approval Note */}
                <div style={{
                  background: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  fontSize: '0.9rem',
                  color: '#92400E'
                }}>
                  <strong>Note:</strong> {featuredEvent.approvalNote}
                </div>

                {/* Registration Button */}
                {!isRegistered ? (
                  <button
                    onClick={handleRegister}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #4A7C5B 0%, #2C5F3F 100%)',
                      color: 'white',
                      padding: '1.25rem 2rem',
                      borderRadius: '12px',
                      border: 'none',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      boxShadow: '0 8px 25px rgba(74, 124, 91, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 35px rgba(74, 124, 91, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 25px rgba(74, 124, 91, 0.3)';
                    }}
                  >
                    Request to Join
                    <ChevronRight size={20} />
                  </button>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem'
                  }}>
                    <CheckCircle size={48} color="#059669" style={{ margin: '0 auto 1rem' }} />
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      color: '#065F46',
                      marginBottom: '0.5rem'
                    }}>
                      Registration Submitted!
                    </div>
                    <div style={{
                      color: '#6B7280',
                      fontSize: '0.95rem'
                    }}>
                      Awaiting host approval. You'll receive an email confirmation soon.
                    </div>
                  </div>
                )}

                {/* Event Details */}
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    <Calendar size={18} color="#4A7C5B" />
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6B7280' }}>Date</div>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>{featuredEvent.date}</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    <Clock size={18} color="#4A7C5B" />
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6B7280' }}>Time</div>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>{featuredEvent.time}</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <MapPin size={18} color="#4A7C5B" />
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6B7280' }}>Location</div>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>{featuredEvent.city}</div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div style={{
                  marginTop: '2rem',
                  paddingTop: '2rem',
                  borderTop: '1px solid #E5E7EB',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#6B7280',
                    marginBottom: '0.5rem'
                  }}>
                    🔒 Secure registration
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#9CA3AF'
                  }}>
                    Your information is protected and won't be shared
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div style={{
          textAlign: 'center',
          color: 'white',
          opacity: 0.8,
          fontSize: '0.9rem'
        }}>
          <p>Have questions? <a href="#" style={{ color: 'white', textDecoration: 'underline' }}>Contact support</a></p>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowShareModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280'
              }}
            >
              <X size={24} />
            </button>

            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: '#1F2937'
            }}>
              Share this Event
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {shareOptions.map((option) => (
                <button
                  key={option.platform}
                  style={{
                    padding: '1rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#F9FAFB';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>{option.icon}</span>
                  {option.platform}
                </button>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '0.75rem'
            }}>
              <input
                type="text"
                value={window.location.href}
                readOnly
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '0.9rem'
                }}
              />
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#4A7C5B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#3D6849';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#4A7C5B';
                }}
              >
                <Copy size={16} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '600px',
            width: '100%',
            position: 'relative',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <button
              onClick={() => setShowRegistrationForm(false)}
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280'
              }}
            >
              <X size={24} />
            </button>

            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              marginBottom: '2rem',
              color: '#1F2937'
            }}>
              Request to Join
            </h2>

            <form onSubmit={handleFormSubmit}>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '1.5rem',
                  color: '#1F2937'
                }}>
                  Your Info
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* Name */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your Name"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="you@email.com"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+91 81234 56789"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      What best describes you? *
                    </label>
                    <select
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s',
                        background: 'white'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    >
                      <option value="">Select one or more</option>
                      {descriptionOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      What is your LinkedIn profile? *
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleInputChange}
                      required
                      placeholder="https://linkedin.com/in/yourprofile"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    />
                  </div>

                  {/* Excitement */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      What excites you most about Nerds Meetup?
                    </label>
                    <textarea
                      name="excitement"
                      value={formData.excitement}
                      onChange={handleInputChange}
                      placeholder="Tell us what you're looking forward to..."
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4A7C5B';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                      }}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #4A7C5B 0%, #2C5F3F 100%)',
                  color: 'white',
                  padding: '1.25rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 8px 25px rgba(74, 124, 91, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(74, 124, 91, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(74, 124, 91, 0.3)';
                }}
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsPage;